import type { EntryPatchRequest, ResolveConflictRequest } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

import { entriesApi, qk } from '@/api';

export function useEntriesList(filters: entriesApi.EntriesFilters, enabled = true) {
  const key = {
    jobId: filters.jobId,
    foremanId: filters.foremanId,
    from: filters.from,
    to: filters.to,
    status: filters.status,
  };
  return useQuery({
    queryKey: qk.entries.list(key),
    queryFn: () => entriesApi.listEntries(filters),
    enabled,
  });
}

export function useEntry(id: string) {
  return useQuery({
    queryKey: qk.entries.detail(id),
    queryFn: () => entriesApi.getEntry(id),
    enabled: !!id,
  });
}

/** Any entry mutation touches the dashboard, the job to-date numbers and lists. */
export function useInvalidateEntry() {
  const qc = useQueryClient();
  return (id?: string) => {
    if (id) void qc.invalidateQueries({ queryKey: qk.entries.detail(id) });
    void qc.invalidateQueries({ queryKey: qk.entries.all() });
    void qc.invalidateQueries({ queryKey: qk.dashboard.all() });
  };
}

export function useApproveEntry(id: string) {
  const invalidate = useInvalidateEntry();
  return useMutation({
    mutationFn: () => entriesApi.approveEntry(id),
    onSuccess: () => invalidate(id),
  });
}

export function useUnapproveEntry(id: string) {
  const invalidate = useInvalidateEntry();
  return useMutation({
    mutationFn: () => entriesApi.unapproveEntry(id),
    onSuccess: () => invalidate(id),
  });
}

export function usePatchEntry(id: string) {
  const invalidate = useInvalidateEntry();
  return useMutation({
    mutationFn: (body: z.input<typeof EntryPatchRequest>) => entriesApi.patchEntry(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useResolveConflict(id: string) {
  const invalidate = useInvalidateEntry();
  return useMutation({
    mutationFn: (body: z.input<typeof ResolveConflictRequest>) =>
      entriesApi.resolveConflict(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useEmailReport(id: string) {
  return useMutation({ mutationFn: (to: string[]) => entriesApi.emailReport(id, to) });
}
